import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import { cancellationNavigationSlice } from 'v3/data/store/cancellationNavigation';
import { ICancellationNavigationState } from 'v3/data/store/cancellationNavigation/types';
import { navigationSlice } from 'v3/data/store/contractCard';
import { contractStepperSlice } from 'v3/data/store/contractCard/stepper';
import { IContractStepperState } from 'v3/data/store/contractCard/stepper/types';
import { IContractNavigationState } from 'v3/data/store/contractCard/types';
import { contractingSlice } from 'v3/data/store/contracting';
import { IContractingState } from 'v3/data/store/contracting/types';
import { deviceSlice } from 'v3/data/store/device';
import { IDevice } from 'v3/data/store/device/types';
import { errorSlice } from 'v3/data/store/error';
import { IErrorState } from 'v3/data/store/error/types';
import { groupsSlice } from 'v3/data/store/group';
import { IGroupsState } from 'v3/data/store/group/types';
import { groupsAdminSlice } from 'v3/data/store/groupAdmin';
import { IGroupAdminState } from 'v3/data/store/groupAdmin/types';
import { installmentsSlice } from 'v3/data/store/installments';
import { IInstallmentsState } from 'v3/data/store/installments/types';
import { loadingSlice } from 'v3/data/store/loading';
import { ILoadingState } from 'v3/data/store/loading/types';
import { onlineContractingSlice } from 'v3/data/store/onlineContracting';
import { IOnlineContractingState } from 'v3/data/store/onlineContracting/types';
import { IContractingPaymentState } from 'v3/data/store/payment/types';
import { personSlice } from 'v3/data/store/person';
import { IPerson } from 'v3/data/store/person/types';
import { preferenceOptionsSlice } from 'v3/data/store/preferenceOptions';
import { IPreferenceOptions } from 'v3/data/store/preferenceOptions/types';
import { productsSlice } from 'v3/data/store/product';
import { IProductState } from 'v3/data/store/product/types';
import { proposalSlice } from 'v3/data/store/proposal';
import { IProposal } from 'v3/data/store/proposal/types';
import { rankSellerSlice } from 'v3/data/store/Rank/RankSeller';
import { ISellerRankState } from 'v3/data/store/Rank/RankSeller/types';
import { rankStoreSlice } from 'v3/data/store/Rank/RankStore';
import { IStoreRankState } from 'v3/data/store/Rank/RankStore/types';
import { sellerSlice } from 'v3/data/store/seller';
import { ISellerState } from 'v3/data/store/seller/types';
import { snackbarSlice } from 'v3/data/store/snackbar';
import { ISnackbarState } from 'v3/data/store/snackbar/types';
import { storesSlice } from 'v3/data/store/stores';
import { IStoreState } from 'v3/data/store/stores/types';
import { tokenSlice } from 'v3/data/store/token';
import { ITokenState } from 'v3/data/store/token/types';
import { paymentSlice } from './payment';
import { voucherSlice } from './voucher';
import { IVoucher } from './voucher/types';

export interface IStore {
  error: IErrorState;
  loading: ILoadingState;
  store: IStoreState;
  snackbar: ISnackbarState;
  contracting: IContractingState;
  payment: IContractingPaymentState;
  installments: IInstallmentsState;
  onlineContracting: IOnlineContractingState;
  token: ITokenState;
  device: IDevice;
  products: IProductState;
  person: IPerson;
  proposal: IProposal;
  navigate: IContractNavigationState;
  contractStepper: IContractStepperState[];
  seller: ISellerState;
  group: IGroupsState;
  groupAdmin: IGroupAdminState;
  cancellationNavigation: ICancellationNavigationState;
  preferenceOption: IPreferenceOptions;
  voucher: IVoucher;
  rankStore: IStoreRankState;
  rankSeller: ISellerRankState;
}

export const store = configureStore<IStore>({
  reducer: {
    error: errorSlice.reducer,
    loading: loadingSlice.reducer,
    store: storesSlice.reducer,
    contracting: contractingSlice.reducer,
    payment: paymentSlice.reducer,
    installments: installmentsSlice.reducer,
    onlineContracting: onlineContractingSlice.reducer,
    token: tokenSlice.reducer,
    device: deviceSlice.reducer,
    products: productsSlice.reducer,
    snackbar: snackbarSlice.reducer,
    person: personSlice.reducer,
    proposal: proposalSlice.reducer,
    navigate: navigationSlice.reducer,
    contractStepper: contractStepperSlice.reducer,
    seller: sellerSlice.reducer,
    group: groupsSlice.reducer,
    groupAdmin: groupsAdminSlice.reducer,
    cancellationNavigation: cancellationNavigationSlice.reducer,
    preferenceOption: preferenceOptionsSlice.reducer,
    voucher: voucherSlice.reducer,
    rankStore: rankStoreSlice.reducer,
    rankSeller: rankSellerSlice.reducer,
  },
});

export const clearStore = () => {
  store.dispatch(() => {});
};

export const useStoreSelector: TypedUseSelectorHook<IStore> = useSelector;
